import base64 from 'base64-js'

const enc = new TextEncoder()

const padBase64String = (val: string): string => {
  let rem = val.length % 4
  while (rem < 4) {
    val += '='
    rem++
  }
  return val
}

const base64urlEncode = (val: ArrayBuffer) =>
  base64
    .fromByteArray(new Uint8Array(val))
    // base64-js encodes in the standard alphabet, WebAuthn payloads have to be base64url
    // (RFC 4648 section 5), which swaps '+/' for '-_' and drops the padding
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

export const getAttestation = async (
  id: string,
  email: string,
  name: string,
  challenge: string
): Promise<PublicKeyCredential | null> => {
  let result

  try {
    result = await navigator.credentials.create({
      publicKey: {
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        challenge: enc.encode(challenge),
        rp: { id: document.domain, name: 'Patient21 GmbH' },
        user: {
          id: enc.encode(id),
          name: email,
          displayName: name,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
      },
    })
  } catch (e) {
    console.log(e)
  }

  return (result as PublicKeyCredential) || null
}

export const verifyAttestation = async (challenge: string, publicKey: string) => {
  const arr = base64.toByteArray(padBase64String(publicKey))

  let result

  try {
    result = await navigator.credentials.get({
      publicKey: {
        challenge: enc.encode(challenge),
        rpId: document.domain,
        allowCredentials: [
          {
            type: 'public-key',
            id: arr.buffer as BufferSource,
          },
        ],
        userVerification: 'required',
      },
    })
  } catch (e) {
    console.error(e)
  }

  return result
}

export const publicKeyCredentialToJSON = (pubKeyCred) => {
  // Browsers serialize the credential themselves since WebAuthn level 3 (Chrome 132, Safari 18),
  // in base64url and including transports/publicKey/publicKeyAlgorithm.
  if (pubKeyCred && typeof pubKeyCred.toJSON === 'function') {
    return pubKeyCred.toJSON()
  }

  if (pubKeyCred instanceof ArrayBuffer) {
    return base64urlEncode(pubKeyCred)
  }

  if (Array.isArray(pubKeyCred)) {
    return pubKeyCred.map(publicKeyCredentialToJSON)
  }

  if (pubKeyCred && typeof pubKeyCred === 'object') {
    const obj = {}

    for (const key in pubKeyCred) {
      const value = pubKeyCred[key]

      // `for...in` also yields the credential's methods (getPublicKey, getTransports, toJSON, ...).
      // Copying a native method onto a plain object makes JSON.stringify invoke it with that object
      // as receiver, which throws "TypeError: Illegal invocation" (D21-40508).
      if (typeof value === 'function') {
        continue
      }

      obj[key] = publicKeyCredentialToJSON(value)
    }

    return obj
  }

  return pubKeyCred
}
